import type { LscMember, LscMemberTeamOption } from '../types';

export type TeamAssignmentMode = 'unassigned' | 'existing' | 'custom';

export interface MemberFormValues {
  display_name: string;
  role: string;
  status: string;
  user_uid: string;
  twitch_login: string;
  twitch_user_id: string;
  current_side_label: string;
  team_assignment_mode: TeamAssignmentMode;
  team_assignment_is_custom: boolean;
  team_assignment_source_team_id: string;
  custom_team_name: string;
  resolve_team_conflict: '' | 'keep_lsc' | 'accept_external' | 'custom';
  stream_platform: 'twitch' | 'youtube';
  youtube_channel: string;
}

export const TEAM_LABEL_MAX_LENGTH = 64;
const TEAM_LABEL_PATTERN = /^[\w\s\-'.&]+$/;

export type MemberTeamAssignmentPatch = {
  current_side_label?: string | null;
  team_assignment_is_custom?: boolean;
  team_assignment_source_team_id?: string | null;
  resolve_team_conflict?: 'keep_lsc' | 'accept_external' | 'custom';
};

export function getMemberTeamAssignmentState(
  member: LscMember | null | undefined
): string | null {
  const state = member?.team_assignment_status?.state;
  if (typeof state === 'string' && state.trim()) {
    return state.trim();
  }
  if (memberHasTeamConflict(member)) {
    return 'open_conflict';
  }
  return null;
}

export function getTeamAssignmentConflictLabels(member: LscMember | null | undefined): {
  lscLabel: string | null;
  externalLabel: string | null;
  state: string | null;
  externalSource: string | null;
} {
  const status = member?.team_assignment_status;
  const lscLabel =
    (typeof status?.lsc_label === 'string' ? status.lsc_label : member?.current_side_label)?.trim() ||
    null;
  const externalLabel = getMemberExternalTeamName(member);
  return {
    lscLabel,
    externalLabel,
    state: getMemberTeamAssignmentState(member),
    externalSource:
      typeof status?.external_source === 'string' && status.external_source.trim()
        ? status.external_source.trim()
        : null,
  };
}

export function memberHasTeamConflict(member: LscMember | null | undefined): boolean {
  if (!member) {
    return false;
  }
  if (member.team_assignment_status && typeof member.team_assignment_status === 'object') {
    return Boolean(member.team_assignment_status.conflict);
  }
  if (!member.source_metadata || typeof member.source_metadata !== 'object') {
    return false;
  }
  return Boolean(member.source_metadata.team_assignment_conflict);
}

export function getMemberExternalTeamName(member: LscMember | null | undefined): string | null {
  const overlayLabel = member?.team_assignment_status?.external_label;
  if (typeof overlayLabel === 'string' && overlayLabel.trim()) {
    return overlayLabel.trim();
  }
  const value = member?.source_metadata?.external_team_name;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function isMemberCustomTeamAssignment(member: LscMember | null | undefined): boolean {
  if (member?.team_assignment_status?.is_custom) {
    return true;
  }
  if (!member?.source_metadata || typeof member.source_metadata !== 'object') {
    return false;
  }
  return Boolean(member.source_metadata.team_assignment_is_custom);
}

export function formatTeamOptionLabel(option: LscMemberTeamOption): string {
  if (option.is_claimed && option.claimed_by_display_name && !option.is_current_member_assignment) {
    return `${option.label} — claimed by ${option.claimed_by_display_name}`;
  }
  return option.label;
}

export function isTeamOptionSelectable(
  option: LscMemberTeamOption,
  currentMemberId?: string | null
): boolean {
  if (!option.is_claimed) {
    return true;
  }
  if (option.is_current_member_assignment) {
    return true;
  }
  if (currentMemberId && option.claimed_by_member_id === currentMemberId) {
    return true;
  }
  return false;
}

export function validateCustomTeamName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'Custom team name is required.';
  }
  if (trimmed.length > TEAM_LABEL_MAX_LENGTH) {
    return `Custom team name must be at most ${TEAM_LABEL_MAX_LENGTH} characters.`;
  }
  if (/<[^>]+>|javascript:|on\w+\s*=/i.test(trimmed)) {
    return 'Custom team name contains invalid characters.';
  }
  if (!TEAM_LABEL_PATTERN.test(trimmed)) {
    return 'Custom team name contains invalid characters.';
  }
  return null;
}

export function deriveTeamAssignmentMode(member: LscMember | null | undefined): TeamAssignmentMode {
  if (!member?.current_side_label?.trim()) {
    return 'unassigned';
  }
  if (isMemberCustomTeamAssignment(member)) {
    return 'custom';
  }
  return 'existing';
}

export function memberFormValuesFromMember(member: LscMember): Pick<
  MemberFormValues,
  | 'current_side_label'
  | 'team_assignment_mode'
  | 'team_assignment_is_custom'
  | 'team_assignment_source_team_id'
  | 'custom_team_name'
  | 'resolve_team_conflict'
> {
  const mode = deriveTeamAssignmentMode(member);
  const meta = member.source_metadata;
  const sourceTeamId =
    meta && typeof meta.source_team_id === 'string' ? meta.source_team_id : '';
  return {
    current_side_label: member.current_side_label ?? '',
    team_assignment_mode: mode,
    team_assignment_is_custom: mode === 'custom',
    team_assignment_source_team_id: sourceTeamId,
    custom_team_name: mode === 'custom' ? member.current_side_label ?? '' : '',
    resolve_team_conflict: '',
  };
}

export function buildMemberTeamAssignmentPatch(
  values: MemberFormValues,
  mode: 'create' | 'edit'
): MemberTeamAssignmentPatch | null {
  if (mode !== 'edit') {
    return null;
  }

  if (values.resolve_team_conflict) {
    const patch: MemberTeamAssignmentPatch = {
      resolve_team_conflict: values.resolve_team_conflict,
    };
    if (values.resolve_team_conflict === 'custom') {
      patch.current_side_label = values.custom_team_name.trim();
      patch.team_assignment_is_custom = true;
    }
    return patch;
  }

  if (values.team_assignment_mode === 'unassigned') {
    return {
      current_side_label: null,
      team_assignment_is_custom: false,
    };
  }

  if (values.team_assignment_mode === 'custom') {
    return {
      current_side_label: values.custom_team_name.trim(),
      team_assignment_is_custom: true,
    };
  }

  const label = values.current_side_label.trim();
  if (!label) {
    return {
      current_side_label: null,
      team_assignment_is_custom: false,
    };
  }

  const patch: MemberTeamAssignmentPatch = {
    current_side_label: label,
    team_assignment_is_custom: false,
  };
  const sourceTeamId = values.team_assignment_source_team_id.trim();
  if (sourceTeamId) {
    patch.team_assignment_source_team_id = sourceTeamId;
  }
  return patch;
}

export function findTeamOptionForLabel(
  teams: LscMemberTeamOption[],
  label: string
): LscMemberTeamOption | undefined {
  const normalized = label.trim().toLowerCase();
  return teams.find((team) => team.label.trim().toLowerCase() === normalized);
}

export function formatMemberTeamDisplay(member: LscMember): string {
  const label = member.current_side_label?.trim();
  return label || 'Unassigned';
}
